import React from "react";
import ContentSectionHeader from "./ContentSectionHeader";
import PageHeader from "./PageHeader";
import { StatValueSkeleton } from "./Skeleton";

interface ScrollableContentLayoutProps {
    /** Page title */
    title: string;
    /** Optional subtitle/description */
    subtitle?: string;
    /** Action buttons (e.g., Create, Export) */
    actions?: React.ReactNode;
    /** Statistics cards to display in the header */
    stats?: Array<{
        label: string;
        value: string | number;
        icon?: React.ReactNode;
        color?: string;
        description?: string | React.ReactNode;
    }>;
    /** Loading state for stats */
    loading?: boolean;
    /** Stat labels for loading state */
    statLabels?: Array<{
        label: string;
        icon?: React.ReactNode;
        color?: string;
    }>;
    /** Main content (tables, lists, etc.) */
    children: React.ReactNode;
    /** Optional additional header content (filters, search, etc.) - appears below PageHeader but above content */
    headerContent?: React.ReactNode;
    /** Optional reusable content section header title */
    contentHeaderTitle?: React.ReactNode;
    /** Optional reusable content section header actions */
    contentHeaderActions?: React.ReactNode;
    /** Optional className for the outer Card */
    className?: string;
}

/**
 * ScrollableContentLayout - A reusable layout component with fixed header and scrollable content
 * 
 * This component implements a Google Drive-style layout where:
 * - Page title, description, and action buttons are fixed at the top
 * - Content area (tables, lists, pagination) is scrollable
 * - Header remains visible when scrolling content
 * 
 * @example
 * ```tsx
 * <ScrollableContentLayout
 *   title="News Center"
 *   subtitle="Manage your news articles"
 *   actions={<Button>Create News</Button>}
 *   stats={statsArray}
 *   headerContent={<FilterRow />}
 * >
 *   <Table>...</Table>
 *   <Pagination />
 * </ScrollableContentLayout>
 * ```
 */
const ScrollableContentLayout: React.FC<ScrollableContentLayoutProps> = ({
    title,
    subtitle,
    actions,
    stats,
    loading = false,
    statLabels,
    children,
    headerContent,
    contentHeaderTitle,
    contentHeaderActions,
    className = "",
}) => {
    return (
        <div className={`bg-background ${className}`}>
            <PageHeader title={title} subtitle={subtitle} actions={actions} sticky />

            {/* Scrollable Content Section - Stats, headerContent, and children */}
            <div className="px-6 py-6">
                    {/* Statistics Section - Using same styling as PageHeader */}
                    {loading && statLabels && statLabels.length > 0 ? (
                        <div className={`grid gap-4 mb-4 ${statLabels.length === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                                statLabels.length === 3 ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3' :
                                    statLabels.length === 5 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5' :
                                        statLabels.length === 6 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6' :
                                        'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'
                            }`}>
                            {statLabels.map((stat, idx) => (
                                <div
                                    key={stat.label + idx}
                                    className={`rounded-lg border border-gray-200 bg-white dark:bg-[#212936] dark:border-[#394150] p-4 relative ${stat.color || ''}`}
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="text-sm text-foreground mb-1">{stat.label}</div>
                                            <div className="text-2xl font-bold">
                                                <StatValueSkeleton />
                                            </div>
                                        </div>
                                        {stat.icon && (
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center ml-4 bg-[#F2FCFA] dark:bg-emerald-900/30">
                                                <div className="text-emerald-600 dark:text-emerald-400">{stat.icon}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : stats && stats.length > 0 ? (
                        <div className={`grid gap-4 mb-4 ${stats.length === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                                stats.length === 3 ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3' :
                                    stats.length === 5 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5' :
                                        stats.length === 6 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6' :
                                        'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'
                            }`}>
                            {stats.map((stat, idx) => (
                                <div
                                    key={stat.label + idx}
                                    className={`rounded-lg border border-gray-200 bg-white dark:bg-[#212936] dark:border-[#394150] p-4 relative ${stat.color || ''}`}
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                                            <div className="text-sm text-foreground mb-1">{stat.label}</div>
                                            {stat.description && <div className="text-xs text-foreground mt-1">{stat.description}</div>}
                                        </div>
                                        {stat.icon && (
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center ml-4 bg-[#F2FCFA] dark:bg-emerald-900/30">
                                                <div className="text-emerald-600 dark:text-emerald-400">{stat.icon}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : null}
                    
                    {contentHeaderTitle ? (
                        <ContentSectionHeader
                            title={contentHeaderTitle}
                            actions={contentHeaderActions}
                        />
                    ) : null}

                    {/* Additional header content (filters, search, etc.) */}
                    {headerContent}

                    {/* Main content */}
                    {children}
                </div>
        </div>
    );
};

export default ScrollableContentLayout;
